'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

const SHOWS_FILTERS_CHANGED_EVENT = 'showsFiltersChanged';

let currentShowsParams: URLSearchParams | null = null;

/**
 * Read the user's last searched city from cross-domain `theatre-searches-city` in localStorage.
 * Shared across Sports, Shows, Restaurants, Hotels.
 */
export function getStoredCity(): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = localStorage.getItem('theatre-searches-city');
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { city?: string } | null;
    return (parsed?.city || '').trim();
  } catch {
    return '';
  }
}

/**
 * Updates filter parameters in memory and notifies all show listing
 * components WITHOUT displaying query strings on the browser URL.
 * Keeps browser URL clean (e.g. `/shows`).
 */
export function replaceShowsSearchParams(pathname: string, params: URLSearchParams) {
  currentShowsParams = new URLSearchParams(params.toString());
  if (typeof window !== 'undefined') {
    if (window.location.search) {
      window.history.replaceState(null, '', pathname);
    }
    window.dispatchEvent(new CustomEvent(SHOWS_FILTERS_CHANGED_EVENT, { detail: currentShowsParams }));
  }
}

/**
 * Non-reactive read of the params the listing is CURRENTLY showing.
 *
 * `replaceShowsSearchParams` deliberately strips the query string off the
 * browser URL (keeping `/shows` clean), so `useSearchParams()` inside the
 * search bar goes empty after the first search and can't be used to ask
 * "is a keyword filter active right now?". This returns the in-memory
 * params the listing actually reads, or `null` before any search has run
 * (in which case the caller should fall back to the URL).
 */
export function getCurrentShowsParams(): URLSearchParams | null {
  return currentShowsParams ? new URLSearchParams(currentShowsParams.toString()) : null;
}

/**
 * Reactive `URLSearchParams` for the `/shows` listing — the single source
 * of truth both `ShowsFilters` and `ShowsList` read from.
 */
export function useShowsQueryParams(): URLSearchParams {
  const routerParams = useSearchParams();
  const [params, setParams] = useState<URLSearchParams>(() => {
    if (routerParams.get('city')) {
      return new URLSearchParams(routerParams.toString());
    }
    if (currentShowsParams) return new URLSearchParams(currentShowsParams.toString());
    return new URLSearchParams(routerParams.toString());
  });

  useEffect(() => {
    function syncParams() {
      const routerCity = routerParams.get('city');
      const targetParams = currentShowsParams
        ? new URLSearchParams(currentShowsParams.toString())
        : new URLSearchParams(routerParams.toString());

      if (routerCity) {
        if (targetParams.get('city') !== routerCity) {
          targetParams.set('city', routerCity);
          currentShowsParams = new URLSearchParams(targetParams.toString());
        }
      } else {
        const storedCity = getStoredCity();
        if (storedCity && targetParams.get('city') !== storedCity) {
          targetParams.set('city', storedCity);
          targetParams.delete('page');
          currentShowsParams = new URLSearchParams(targetParams.toString());
        }
      }

      setParams((prev) => (prev.toString() === targetParams.toString() ? prev : targetParams));
    }

    syncParams();

    function onChange(e: Event) {
      const customEvent = e as CustomEvent<URLSearchParams>;
      const next = customEvent.detail || currentShowsParams || new URLSearchParams();
      setParams(new URLSearchParams(next.toString()));
    }

    function onStorage(e: StorageEvent) {
      if (e.key === 'theatre-searches-city') {
        syncParams();
      }
    }

    window.addEventListener(SHOWS_FILTERS_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SHOWS_FILTERS_CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [routerParams]);

  return params;
}

